import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import CTA from 'Src/components/buttons/CTA';
import { Flex } from 'Src/components/layout/Flex';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';

import styles from './CashBankBillCount.module.scss';

class FormModel {
  dollar1: number | undefined = undefined;
  dollar5: number | undefined = undefined;
  dollar10: number | undefined = undefined;
  dollar20: number | undefined = undefined;
  dollar50: number | undefined = undefined;
  dollar100: number | undefined = undefined;
  coin1: number | undefined = undefined;
  coin5: number | undefined = undefined;
  coin10: number | undefined = undefined;
  coin25: number | undefined = undefined;
  coin50: number | undefined = undefined;
  coin100: number | undefined = undefined;

  constructor(part?: Partial<FormModel>) {
    if (part) Object.assign(this, part);
  }
}

const dollarFields = [
  { name: 'dollar1', label: '$1' },
  { name: 'dollar5', label: '$5' },
  { name: 'dollar10', label: '$10' },
  { name: 'dollar20', label: '$20' },
  { name: 'dollar50', label: '$50' },
  { name: 'dollar100', label: '$100' },
];

const coinFields = [
  { name: 'coin1', label: '¢1' },
  { name: 'coin5', label: '¢5' },
  { name: 'coin10', label: '¢10' },
  { name: 'coin25', label: '¢25' },
  { name: 'coin50', label: '¢50' },
  { name: 'coin100', label: '$1 (Coin)' },
];

const { Form, Field } = getTypedFormComponents<FormModel>();

interface CashBankBillCountProps {
  onCountComplete: (formValues: FormModel) => void;
}

const CashBankBillCount: FC<CashBankBillCountProps> = ({ onCountComplete }) => {
  const { t } = useTranslation();

  const form = useForm<FormModel>({
    defaultValues: { ...new FormModel() },
    schema: object().shape({
      dollar1: number().integer().label('$1').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      dollar5: number().integer().label('$5').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      dollar10: number().integer().label('$10').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      dollar20: number().integer().label('$20').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      dollar50: number().integer().label('$50').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      dollar100: number().integer().label('$100').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      coin1: number().integer().label('¢1').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      coin5: number().integer().label('¢5').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      coin10: number().integer().label('¢10').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      coin25: number().integer().label('¢25').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      coin50: number().integer().label('¢50').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
      coin100: number().integer().label('$1 (Coin)').transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().moreThan(-1),
    }),
  });

  const onCount = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      onCountComplete(values);
    });
  };

  return (
    <Form usedForm={form}>
      <Flex horizontal className={styles.formFieldWrapper}>
        <Flex fullWidth>
          {dollarFields.map(({ name, label }, i) => (
            <Field key={`${name}-${i}`} type="number" name={name as keyof FormModel} label={label} />
          ))}
        </Flex>
        <Flex fullWidth>
          {coinFields.map(({ name, label }, i) => (
            <Field key={`${name}-${i}`} type="number" name={name as keyof FormModel} label={label} />
          ))}
        </Flex>
      </Flex>
      <CTA
        color="primary"
        onClick={() => void onCount()}
        label={t('cashBank.labels.countTheBills')}
      />
    </Form>
  );
};

export default CashBankBillCount;
export { CashBankBillCountProps, Field, Form, FormModel };
